import { motion, type HTMLMotionProps, type Transition, type Variants } from 'motion/react';
import type { ReactNode } from 'react';

/**
 * Primitivos de movimento do sistema — design.md §10.
 *
 * Tudo que entra na tela entra do mesmo jeito: opacidade e um deslocamento
 * curto para cima, com a curva `ease-out-quint`. Lista escalona os itens em
 * passos de 40ms, e nunca além do oitavo — o nono já está em tela quando o
 * olho chega nele, e esperar um segundo por uma grade de 24 cartões seria o
 * contrário do que a animação existe para dar.
 *
 * Quem pediu menos movimento ao sistema recebe só a troca de opacidade: o
 * `MotionConfig` em `App.tsx` desliga os transforms.
 */

export const EASE_OUT_QUINT = [0.2, 0, 0, 1] as const;

export const TRANSITION = {
  instant: { duration: 0.1, ease: 'easeOut' } satisfies Transition,
  fast: { duration: 0.16, ease: EASE_OUT_QUINT } satisfies Transition,
  base: { duration: 0.24, ease: EASE_OUT_QUINT } satisfies Transition,
  slow: { duration: 0.5, ease: 'easeOut' } satisfies Transition,
  /** Mola curta e sem quique, para o que muda de lugar (indicador de aba, item ativo). */
  layout: { type: 'spring', stiffness: 520, damping: 44, mass: 0.8 } satisfies Transition,
} as const;

/** Até onde o escalonamento vai: a partir daqui todo item entra junto. */
const MAX_STAGGERED = 8;
const STAGGER_STEP = 0.04;

export const rise: Variants = {
  hidden: { opacity: 0, y: 8 },
  shown: { opacity: 1, y: 0, transition: TRANSITION.base },
};

export const stagger: Variants = {
  hidden: {},
  shown: { transition: { staggerChildren: STAGGER_STEP, delayChildren: 0.02 } },
};

/**
 * Bloco que sobe ao entrar.
 *
 * Com `index`, o atraso é calculado aqui, sem depender de um contêiner: é o
 * que serve a uma grade cujo número de itens muda de página para página.
 *
 * Não usa as variantes de cima de propósito: a transição declarada dentro de
 * uma variante vence a da propriedade `transition`, e o atraso por índice
 * seria ignorado.
 */
export function Rise({
  index,
  children,
  ...rest
}: HTMLMotionProps<'div'> & { index?: number | undefined; children?: ReactNode }) {
  const delay = index === undefined ? 0 : Math.min(index, MAX_STAGGERED) * STAGGER_STEP;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...TRANSITION.base, delay }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * Contêiner que escalona a entrada dos filhos `<Rise>` diretos.
 *
 * Os filhos herdam `hidden`/`shown` daqui; não precisam de `initial` nem de
 * `animate` próprios — se tiverem, o escalonamento é ignorado.
 */
export function Stagger({ children, ...rest }: HTMLMotionProps<'div'> & { children?: ReactNode }) {
  return (
    <motion.div variants={stagger} initial="hidden" animate="shown" {...rest}>
      {children}
    </motion.div>
  );
}

/** Filho de `<Stagger>`: só declara a variante, o tempo vem do pai. */
export function StaggerItem({
  children,
  ...rest
}: HTMLMotionProps<'div'> & { children?: ReactNode }) {
  return (
    <motion.div variants={rise} {...rest}>
      {children}
    </motion.div>
  );
}

/**
 * Transição de rota: a página nova sobe ao entrar. Só o conteúdo anima; a
 * barra lateral é a âncora espacial e não se move.
 *
 * Entrada apenas, sem saída. Para animar a saída seria preciso congelar a
 * página antiga enquanto a nova carrega, e o `<Outlet>` do roteador não
 * congela: dentro do bloco que sai ele já renderizaria a rota nova. A chave
 * pelo caminho remonta o bloco a cada navegação, e é isso que dispara a
 * entrada.
 *
 * A página de tela cheia (o mapa) só aparece, sem subir: colada nas bordas da
 * janela, subir 8px abriria uma fresta embaixo durante a entrada.
 */
export function PageTransition({
  pathname,
  children,
  subir = true,
  className,
}: {
  pathname: string;
  children: ReactNode;
  subir?: boolean | undefined;
  className?: string | undefined;
}) {
  return (
    <motion.div
      key={pathname}
      initial={{ opacity: 0, y: subir ? 8 : 0 }}
      animate={{ opacity: 1, y: 0 }}
      transition={TRANSITION.base}
      className={className}
    >
      {children}
    </motion.div>
  );
}
